import { __assign, __read } from "tslib";
import { getIsMobileOrTablet } from '@office-start/device-detection-utility';
import { useResponsiveBreakpoint, } from '@office-start/dom-utility';
import { isEnterOrSpace } from '@office-start/keystroke-handler-utility';
import { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { CarouselPeek, } from './../model';
import { useKeyboardNavigation } from './useKeyboardNavigation';
export var useNavigation = function (navigationParameters) {
    var arrowKeyListNavEnabled = navigationParameters.arrowKeyListNavEnabled, builtInButtons = navigationParameters.builtInButtons, callbacks = navigationParameters.callbacks, cards = navigationParameters.cards, cardGutterWidth = navigationParameters.cardGutterWidth, maybeCarouselResources = navigationParameters.carouselResources, getItemDimensionsForBreakpoint = navigationParameters.getItemDimensionsForBreakpoint, hideNavWhenDisabled = navigationParameters.hideNavWhenDisabled, isHeaderCollapsed = navigationParameters.isHeaderCollapsed, isRtl = navigationParameters.isRtl, isVisualRefresh = navigationParameters.isVisualRefresh, resizeOptions = navigationParameters.resizeOptions, isDesktopNav = navigationParameters.isDesktopNav;
    // Based on Fluent responsive breakpoints, one can optionally resize the Carousel cards.
    var breakpoint = useResponsiveBreakpoint();
    // Need to run when component is loaded
    var loggedViewRef = useRef(false);
    // Based on the current breakpoint, compute the dimensions of each Carousel card.
    var itemDimensions = useMemo(function () { return getItemDimensionsForBreakpoint(breakpoint); }, [breakpoint, getItemDimensionsForBreakpoint]);
    // Keep track of the focused index to reset it here if keyboard nav stops
    var focusedIndex = useRef(null);
    // Keep track between renders (page navs) whether or not we're navigating by keyboard
    var isNavigatingByKeyboardRef = useRef(null);
    var _a = __read(useState(0), 2), containerWidthState = _a[0], setContainerWidthState = _a[1];
    // State that would be changed on resize
    var _b = __read(useState({
        allCardsFit: false,
        cardsPerPage: 0,
        pageCount: 1,
        cardsOnLastPage: 0,
    }), 2), resizeState = _b[0], setResizeState = _b[1];
    var _c = __read(useState({
        displayedPage: 1,
        navigationDirection: undefined,
        startIndex: 0,
        stopIndex: 0,
    }), 2), navigationState = _c[0], setNavigationState = _c[1];
    // Determine whether or not there are more carousel pages to show.
    var isLastPage = useMemo(function () {
        return (resizeState.cardsPerPage === 0 ||
            navigationState.displayedPage === resizeState.pageCount ||
            resizeState.allCardsFit);
    }, [
        resizeState.cardsPerPage,
        resizeState.pageCount,
        resizeState.allCardsFit,
        navigationState.displayedPage,
    ]);
    // Determine whether or not the user is on the first page.
    var isFirstPage = useMemo(function () {
        return (navigationState.displayedPage === 1 ||
            (resizeState.allCardsFit && navigationState.startIndex === 0));
    }, [
        navigationState.displayedPage,
        resizeState.allCardsFit,
        navigationState.startIndex,
    ]);
    /**
     * Get the index at which rendering should stop
     * Exclusive: stop index will not be rendered!
     *
     * For Resize CarouselPeek type, keep stop index as is (no partial cards should be rendered)
     * For all other types, draw a partial card
     */
    var getStopIndex = useCallback(function (startIndex, cardsPerPage) {
        var extraStop = resizeOptions.type === CarouselPeek.Resize ? 0 : 1;
        return Math.min(cards.length, startIndex + cardsPerPage + extraStop);
    }, [cards.length, resizeOptions.type]);
    // Used to detect whether the container can contain all available cards.
    var onResize = useCallback(function (containerDimensions) {
        setContainerWidthState(containerDimensions.width);
    }, []);
    useEffect(function () {
        var _a, _b;
        var itemWidthPlusGutter = itemDimensions.width + cardGutterWidth;
        // How many pixels to fit it all?
        var widthToFitAllCards = cards.length * itemWidthPlusGutter - cardGutterWidth;
        var itemMinWidthPlusGutter = (_a = itemDimensions.minWidth) !== null && _a !== void 0 ? _a : itemDimensions.width + cardGutterWidth;
        var minWidthToFitAllCards = cards.length * itemMinWidthPlusGutter - cardGutterWidth;
        // Cards per page?
        var fullCardsPerPage = containerWidthState
            ? Math.max(Math.floor(containerWidthState / itemWidthPlusGutter), 1)
            : 1;
        // For Fixed/Peek cards, make sure that the remaining space is distributed in a way that
        // meets min/max/width requirements.
        if (resizeOptions.type === CarouselPeek.Fixed) {
            var leftOver = containerWidthState - fullCardsPerPage * itemWidthPlusGutter;
            // Redistribute any leftover width when adding the peek
            var itemWidthForPeek = itemDimensions.width;
            if (leftOver >= resizeOptions.peekSizePixels) {
                itemWidthForPeek +=
                    (leftOver - resizeOptions.peekSizePixels) /
                        fullCardsPerPage;
            }
            else if (leftOver > 0) {
                itemWidthForPeek -=
                    (resizeOptions.peekSizePixels - leftOver) /
                        fullCardsPerPage;
            }
            // If newly calculated width is too big or too small, remove or add one card
            if (itemDimensions.maxWidth !== undefined &&
                itemWidthForPeek > itemDimensions.maxWidth) {
                fullCardsPerPage++;
            }
            else if (itemDimensions.minWidth !== undefined &&
                itemWidthForPeek < itemDimensions.minWidth) {
                fullCardsPerPage = Math.max(1, fullCardsPerPage - 1);
            }
        }
        var pageCount = Math.ceil(cards.length / fullCardsPerPage);
        var fullCardsOnLastPage = cards.length - fullCardsPerPage * (pageCount - 1);
        setResizeState({
            cardsPerPage: fullCardsPerPage,
            pageCount: pageCount,
            allCardsFit: minWidthToFitAllCards <= containerWidthState,
            cardsOnLastPage: fullCardsOnLastPage,
        });
        // Need to recalculate last page here, because it is using the stale value
        // We have just calculated pageCount and cardsPerPage/cardsOnLastPage, but isLastPage from the useMemo
        // is still using the old values.
        var isLastPage = fullCardsPerPage === 0 ||
            navigationState.displayedPage === pageCount ||
            widthToFitAllCards <= containerWidthState;
        // Stop index should not be rendered.
        var stopIndex = getStopIndex(navigationState.startIndex, isLastPage ? fullCardsOnLastPage : fullCardsPerPage);
        // On resize, start index / page should stay the same as it was
        setNavigationState(function (previous) {
            return __assign(__assign({}, previous), { stopIndex: stopIndex });
        });
        // This callback needs to be called when the initial cards are loaded on the Home page when the FeedbackAPI is called for 'View' action for the initial cards.
        if (!loggedViewRef.current && containerWidthState !== 0) {
            (_b = callbacks === null || callbacks === void 0 ? void 0 : callbacks.onCarouselNavClick) === null || _b === void 0 ? void 0 : _b.call(callbacks, 1, fullCardsPerPage, cards.length, 1 /* CarouselNavigationDirection.NavNext */);
            loggedViewRef.current = true;
        }
    }, [
        containerWidthState,
        itemDimensions.width,
        itemDimensions.maxWidth,
        itemDimensions.minWidth,
        cardGutterWidth,
        cards.length,
        resizeOptions,
        navigationState.displayedPage,
        navigationState.startIndex,
        getStopIndex,
        callbacks,
    ]);
    // Detects whether or not the user is using a mobile / tablet device.
    // Used to make the list "swipe-able" in mobile mode, and disable Carousel navigation.
    var isMobileOrTabletDevice = useMemo(function () {
        return getIsMobileOrTablet();
    }, []);
    // function to update navigation state
    var updateNavigationState = useCallback(function (navigationDirection, targetPage, pageCount, cardsOnLastPage, requestedIndex) {
        // Indicates we will be navigating to the final page in this navigation.
        var isTargetingLastPage = targetPage === pageCount;
        var targetIndex;
        var firstItemIndex;
        if (isTargetingLastPage) {
            // The last page should be aligned with the "end" property, aligning the list to the right.
            targetIndex = cards.length;
            firstItemIndex = Math.max(0, targetIndex - cardsOnLastPage);
            // Make sure we show enough cards on the last page
            if (resizeOptions.type === CarouselPeek.Fixed) {
                firstItemIndex = Math.max(firstItemIndex - 1, 0);
            }
        }
        else {
            targetIndex = Math.max(0, Math.min(cards.length, resizeState.cardsPerPage * (targetPage - 1)));
            firstItemIndex = targetIndex;
        }
        // Update keyboard refs
        if (arrowKeyListNavEnabled && requestedIndex !== undefined) {
            isNavigatingByKeyboardRef.current = true;
        }
        else {
            focusedIndex.current = null;
        }
        var stopIndex = getStopIndex(firstItemIndex, isTargetingLastPage
            ? cardsOnLastPage
            : resizeState.cardsPerPage);
        setNavigationState({
            displayedPage: targetPage,
            navigationDirection: navigationDirection,
            startIndex: firstItemIndex,
            stopIndex: stopIndex,
        });
    }, [
        arrowKeyListNavEnabled,
        cards.length,
        getStopIndex,
        resizeOptions.type,
        resizeState.cardsPerPage,
    ]);
    // Callback used when the user presses one of the Carousel navigation buttons.
    var navigateCards = useCallback(function (navigationDirection, requestedIndex) {
        var _a, _b;
        // The target page we will be navigating to.
        // Bound between 1 and max number of pages inclusive.
        var targetPage = Math.max(1, Math.min(navigationState.displayedPage + navigationDirection, resizeState.pageCount));
        updateNavigationState(navigationDirection, targetPage, resizeState.pageCount, resizeState.cardsOnLastPage, requestedIndex);
        // Callbacks?
        if (navigationDirection !== 0 /* CarouselNavigationDirection.NavSame */) {
            (_a = callbacks === null || callbacks === void 0 ? void 0 : callbacks.onCarouselNavClick) === null || _a === void 0 ? void 0 : _a.call(callbacks, targetPage, resizeState.cardsPerPage, cards.length, navigationDirection);
            (_b = callbacks === null || callbacks === void 0 ? void 0 : callbacks.onUserClick) === null || _b === void 0 ? void 0 : _b.call(callbacks);
        }
    }, [
        navigationState.displayedPage,
        resizeState.pageCount,
        resizeState.cardsOnLastPage,
        resizeState.cardsPerPage,
        updateNavigationState,
        callbacks,
        cards.length,
    ]);
    // update both navigation and resize state when we remove a card from the carousel.
    var updateNavAndResizeState = useCallback(function (navigationDirection) {
        var pageCount = Math.ceil(cards.length / resizeState.cardsPerPage);
        var fullCardsOnLastPage = cards.length - resizeState.cardsPerPage * (pageCount - 1);
        // On resize, start index / page should stay the same as it was
        setResizeState(function (previous) {
            return __assign(__assign({}, previous), { pageCount: pageCount, fullCardsOnLastPage: fullCardsOnLastPage });
        });
        // Bound between 1 and max number of pages inclusive.
        var targetPage = Math.max(1, Math.min(navigationState.displayedPage + navigationDirection, pageCount));
        updateNavigationState(navigationDirection, targetPage, pageCount, fullCardsOnLastPage);
    }, [
        cards.length,
        resizeState.cardsPerPage,
        navigationState.displayedPage,
        updateNavigationState,
    ]);
    useEffect(function () {
        // If the stop index is past the length of cards
        // We need to recalculate the navigationState and resizeState
        if (navigationState.stopIndex > cards.length) {
            updateNavAndResizeState(0 /* CarouselNavigationDirection.NavSame */);
        }
    }, [cards, navigationState, navigateCards, updateNavAndResizeState]);
    var navPrevious = useCallback(function (previousRequestedIndex) {
        navigateCards(-1 /* CarouselNavigationDirection.NavPrevious */, previousRequestedIndex);
    }, [navigateCards]);
    var navNext = useCallback(function (nextRequestedIndex) {
        navigateCards(1 /* CarouselNavigationDirection.NavNext */, nextRequestedIndex);
    }, [navigateCards]);
    var handleNavKeyDown = useCallback(function (event, navigationDirection) {
        if (!isEnterOrSpace(event)) {
            return;
        }
        event.preventDefault();
        navigateCards(navigationDirection);
    }, [navigateCards]);
    var hideNavigationControls = useMemo(function () {
        return (
        // Hide nav when only one page (per prop. As seen in 'CreateTilesList')
        (hideNavWhenDisabled && isFirstPage && isLastPage) ||
            // Requested to use built in buttons (no usages)
            builtInButtons ||
            // on tablets or mobile
            (isMobileOrTabletDevice && !isDesktopNav));
    }, [
        builtInButtons,
        hideNavWhenDisabled,
        isFirstPage,
        isLastPage,
        isMobileOrTabletDevice,
        isDesktopNav,
    ]);
    var carouselResources = useMemo(function () {
        // $TODO: There are consumers today who do not pass carouselResources.
        // This is the existing fallback that should be removed
        // when resources are required.
        if (!maybeCarouselResources) {
            return {
                collapseHeaderAriaLabel: 'Enter key to collapse this section',
                expandHeaderAriaLabel: 'Enter key to expand this section',
                previousAriaLabel: 'Previous',
                previousTooltip: 'Previous',
                nextAriaLabel: 'Next',
                nextTooltip: 'Next',
                navigationAriaDescription: 'Page {0} of {1}',
            };
        }
        return maybeCarouselResources;
    }, [maybeCarouselResources]);
    var builtInButtonOptions = useMemo(function () {
        // If consumer requested built in buttons, or on mobile, show the paddles
        if (builtInButtons || (isMobileOrTabletDevice && !isDesktopNav)) {
            return {
                carouselResources: carouselResources,
                isFirstPage: isFirstPage,
                isLastPage: isLastPage,
                navNext: navNext,
                navPrevious: navPrevious,
            };
        }
        return undefined;
    }, [
        builtInButtons,
        carouselResources,
        isFirstPage,
        isLastPage,
        isMobileOrTabletDevice,
        navNext,
        navPrevious,
        isDesktopNav,
    ]);
    var navigationOptions = useMemo(function () {
        return {
            hideNavigationControls: hideNavigationControls,
            navControlProps: {
                carouselResources: carouselResources,
                disableNavigationControls: isHeaderCollapsed,
                handleNavKeyDown: handleNavKeyDown,
                isFirstPage: isFirstPage,
                isLastPage: isLastPage,
                isRtl: isRtl,
                isVisualRefresh: isVisualRefresh,
                navNext: navNext,
                navPrevious: navPrevious,
                displayedPage: navigationState.displayedPage,
                pageCount: resizeState.pageCount,
            },
        };
    }, [
        hideNavigationControls,
        carouselResources,
        isHeaderCollapsed,
        handleNavKeyDown,
        isFirstPage,
        isLastPage,
        isRtl,
        isVisualRefresh,
        navNext,
        navPrevious,
        navigationState.displayedPage,
        resizeState.pageCount,
    ]);
    var keyboardNavigationOptions = useKeyboardNavigation({
        arrowKeyListNavEnabled: arrowKeyListNavEnabled,
        cardsPerPage: resizeState.cardsPerPage,
        isLastPage: isLastPage,
        isNavigatingByKeyboardRef: isNavigatingByKeyboardRef,
        isRtl: isRtl,
        firstItemIndex: navigationState.startIndex,
        focusedIndex: focusedIndex,
        navNext: navNext,
        navPrevious: navPrevious,
        numberOfItems: cards.length,
    });
    return useMemo(function () {
        return {
            builtInButtonOptions: builtInButtonOptions,
            isMobileOrTabletDevice: isMobileOrTabletDevice,
            keyboardNavigationOptions: keyboardNavigationOptions,
            navigationOptions: navigationOptions,
            navigationState: navigationState,
            onResize: onResize,
            resizeState: resizeState,
        };
    }, [
        builtInButtonOptions,
        isMobileOrTabletDevice,
        keyboardNavigationOptions,
        navigationOptions,
        navigationState,
        onResize,
        resizeState,
    ]);
};
//# sourceMappingURL=useNavigation.js.map